import React, { useState, useEffect, useRef } from 'react';
import { X, Save, FileText, Users, ClipboardList, MoreHorizontal, MapPin, Loader2, Building2, UserPlus, Phone, AlertTriangle } from 'lucide-react';
import { Project, ProjectType, SchoolAddress, ProjectUser, SchoolSegment } from '../../types/project.types';
import { SchoolSegmentsEditor } from './SchoolSegmentsEditor';
import { Organization } from '../../types/organization.types';
import { UserRole } from '../../types/user.types';
import { projectService } from '../../services/project.service';
import { organizationService } from '../../services/organization.service';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import { Timestamp } from 'firebase/firestore';
import { contractGenerationService } from '../../services/contractGeneration.service';
import { geocodingService } from '../../services/geocoding.service';
import { US_TIMEZONES, getTimezoneAbbreviation } from '../../utils/dateUtils';

const PROJECT_TYPE_CONFIG: Record<ProjectType, { label: string; icon: React.FC<any>; color: string }> = {
  lead_program: { label: 'Lead Program', icon: FileText, color: 'bg-blue-100 text-blue-800' },
  enrollment: { label: 'Enrollment', icon: Users, color: 'bg-purple-100 text-purple-800' },
  survey: { label: 'Survey', icon: ClipboardList, color: 'bg-green-100 text-green-800' },
  other: { label: 'Other', icon: MoreHorizontal, color: 'bg-gray-100 text-gray-800' },
};

interface EditProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  project: Project;
  onProjectUpdated: () => void;
}

export const EditProjectModal: React.FC<EditProjectModalProps> = ({
  isOpen,
  onClose,
  project,
  onProjectUpdated
}) => {
  const { userProfile } = useAuth();
  const [saving, setSaving] = useState(false);
  const [isPrimaryAdmin, setIsPrimaryAdmin] = useState(false);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const projectType = project.projectType || 'lead_program'; // Default for legacy projects

  // Check if user is primary admin of their organization
  useEffect(() => {
    const checkPrimaryAdmin = async () => {
      if (!userProfile?.organizationId) {
        setIsPrimaryAdmin(false);
        return;
      }
      try {
        const org = await organizationService.get(userProfile.organizationId);
        setIsPrimaryAdmin(org?.primaryAdminId === userProfile.uid);
      } catch (error) {
        console.error('Error checking primary admin status:', error);
        setIsPrimaryAdmin(false);
      }
    };
    checkPrimaryAdmin();
  }, [userProfile?.organizationId, userProfile?.uid]);

  // Load organizations for provider_admin
  useEffect(() => {
    if (userProfile?.role === 'provider_admin') {
      organizationService.getActive().then(setOrganizations).catch(console.error);
    }
  }, [userProfile]);

  // Fiscal privacy: provider_admin OR primary admin of an org can see bill rate
  // Only provider roles can see/edit internal pay rates (margin protection)
  const canEditBilling = userProfile?.role === 'provider_admin' || isPrimaryAdmin;
  const isProvider = userProfile?.role === 'provider_admin' || userProfile?.role === 'provider_user';

  const [formData, setFormData] = useState({
    organizationId: project.organizationId || '',
    name: project.name,
    description: project.description,
    status: project.status,
    // Goals - shared
    validLeads: project.goals.validLeads,
    enrollmentTarget: project.goals.enrollmentTarget,
    conversionRateTarget: project.goals.conversionRateTarget,
    // Survey-specific goals (stored in goals object or custom fields)
    totalDoors: (project as any).totalDoors || 0,
    anticipatedSurveyRate: (project as any).anticipatedSurveyRate || 10,
    surveyGoal: (project as any).surveyGoal || project.goals.validLeads || 0,
    // Billing
    ratePerValidLead: project.settings.billingInfo.ratePerValidLead,
    payRate: project.settings.billingInfo.payRate || 3,
    paymentTerms: project.settings.billingInfo.paymentTerms,
    // Settings
    requireValidation: project.settings.requireValidation,
    validationDeadlineDays: project.settings.validationDeadlineDays,
    // Timezone
    timezone: project.settings?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
    // School address
    schoolAddress: project.schoolAddress?.address || '',
    schoolCity: project.schoolAddress?.city || '',
    schoolState: project.schoolAddress?.state || '',
    schoolZipCode: project.schoolAddress?.zipCode || '',
    schoolPhone: project.schoolAddress?.schoolPhone || '',
    dailyHubspotExport: project.dailyHubspotExport || false,
    autoInvoiceEnabled: project.autoInvoiceEnabled || false,
    automatedEmailsEnabled: project.automatedEmailsEnabled || false,
    appointmentEmails: project.settings?.appointmentEmails || false,
    leadCapturedEmails: project.settings?.leadCapturedEmails || false,
    countPerHousehold: project.settings?.countPerHousehold || false
  });
  const [geocodingAddress, setGeocodingAddress] = useState(false);
  const [schoolSegments, setSchoolSegments] = useState<SchoolSegment[]>(project.schoolSegments || []);

  // Team management state
  const [orgUsers, setOrgUsers] = useState<{ uid: string; name: string; email: string; role: UserRole; projects: string[] }[]>([]);
  const [teamMembers, setTeamMembers] = useState<ProjectUser[]>(project.assignedUsers || []);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [loadingOrgUsers, setLoadingOrgUsers] = useState(false);
  const [showAmendmentModal, setShowAmendmentModal] = useState(false);
  const [pendingChangedTerms, setPendingChangedTerms] = useState<{ field: string; oldValue: string; newValue: string }[]>([]);
  const [generatingAmendment, setGeneratingAmendment] = useState(false);

  // Track original material field values for amendment detection
  const originalMaterialFields = useRef({
    ratePerValidLead: project.settings.billingInfo.ratePerValidLead,
    validLeads: project.goals.validLeads,
    enrollmentTarget: project.goals.enrollmentTarget,
  });

  // Load organization users for team management
  useEffect(() => {
    const loadOrgUsers = async () => {
      const orgId = project.organizationId || userProfile?.organizationId;
      if (!orgId) return;
      setLoadingOrgUsers(true);
      try {
        const users = await projectService.getOrganizationUsers(orgId);
        // Also load provider users if the current user is a provider
        if (userProfile?.role === 'provider_admin' || userProfile?.role === 'provider_user') {
          const providerUsers = await projectService.getOrganizationUsers(userProfile.organizationId);
          const allUsers = [...users];
          for (const pu of providerUsers) {
            if (!allUsers.some(u => u.uid === pu.uid)) {
              allUsers.push(pu);
            }
          }
          setOrgUsers(allUsers);
        } else {
          setOrgUsers(users);
        }
      } catch (error) {
        console.error('Error loading org users:', error);
      } finally {
        setLoadingOrgUsers(false);
      }
    };
    loadOrgUsers();
  }, [project.organizationId, userProfile?.organizationId, userProfile?.role]);

  // Detect material changes that affect the service agreement
  const detectMaterialChanges = (): { field: string; oldValue: string; newValue: string }[] => {
    const changes: { field: string; oldValue: string; newValue: string }[] = [];
    const orig = originalMaterialFields.current;

    if (formData.ratePerValidLead !== orig.ratePerValidLead) {
      changes.push({
        field: 'Bill Rate per Valid Lead',
        oldValue: `$${orig.ratePerValidLead.toFixed(2)}`,
        newValue: `$${formData.ratePerValidLead.toFixed(2)}`
      });
    }
    if (formData.validLeads !== orig.validLeads) {
      changes.push({
        field: 'Valid Lead Goal',
        oldValue: orig.validLeads.toString(),
        newValue: formData.validLeads.toString()
      });
    }
    if (formData.enrollmentTarget !== orig.enrollmentTarget) {
      changes.push({
        field: 'Enrollment Target',
        oldValue: orig.enrollmentTarget.toString(),
        newValue: formData.enrollmentTarget.toString()
      });
    }

    // Check budget cap change
    const oldBudgetCap = orig.validLeads * orig.ratePerValidLead;
    const newBudgetCap = formData.validLeads * formData.ratePerValidLead;
    if (oldBudgetCap !== newBudgetCap && changes.length > 0) {
      changes.push({
        field: 'Budget Cap',
        oldValue: `$${oldBudgetCap.toLocaleString()}`,
        newValue: `$${newBudgetCap.toLocaleString()}`
      });
    }

    return changes;
  };

  const handleSubmit = async () => {
    // Check for material changes that need an amendment
    if (project.serviceAgreementId) {
      const changes = detectMaterialChanges();
      if (changes.length > 0) {
        setPendingChangedTerms(changes);
        setShowAmendmentModal(true);
        return; // Don't save yet — wait for amendment modal decision
      }
    }
    await doSave(false);
  };

  const doSave = async (generateAmendment: boolean) => {
    setSaving(true);
    try {
      // Get selected organization name if changed
      const selectedOrg = organizations.find(o => o.id === formData.organizationId);

      const updateData: Record<string, any> = {
        name: formData.name,
        description: formData.description,
        status: formData.status,
        organizationId: formData.organizationId || '',
        organizationName: selectedOrg?.name || '',
        goals: {
          validLeads: projectType === 'survey' ? formData.surveyGoal : formData.validLeads,
          enrollmentTarget: formData.enrollmentTarget,
          conversionRateTarget: formData.conversionRateTarget
        },
        'settings.billingInfo.ratePerValidLead': formData.ratePerValidLead,
        'settings.billingInfo.payRate': formData.payRate,
        'settings.billingInfo.paymentTerms': formData.paymentTerms,
        'settings.requireValidation': formData.requireValidation,
        'settings.validationDeadlineDays': formData.validationDeadlineDays,
        'settings.timezone': formData.timezone,
        // Drop blank-named segments so we don't persist half-filled rows.
        schoolSegments: schoolSegments.filter(s => s.name.trim().length > 0),
        dailyHubspotExport: formData.dailyHubspotExport,
        autoInvoiceEnabled: formData.autoInvoiceEnabled,
        automatedEmailsEnabled: formData.automatedEmailsEnabled,
        'settings.appointmentEmails': formData.appointmentEmails,
        'settings.leadCapturedEmails': formData.leadCapturedEmails,
        'settings.countPerHousehold': formData.countPerHousehold,
        updatedAt: Timestamp.now()
      };

      // Add survey-specific fields
      if (projectType === 'survey') {
        updateData.totalDoors = formData.totalDoors;
        updateData.anticipatedSurveyRate = formData.anticipatedSurveyRate;
        updateData.surveyGoal = formData.surveyGoal;
      }

      // Handle school address update
      if (formData.schoolAddress) {
        const fullAddress = `${formData.schoolAddress}, ${formData.schoolCity}, ${formData.schoolState} ${formData.schoolZipCode}`;
        try {
          const geocodeResult = await geocodingService.geocodeAddress(fullAddress);
          updateData.schoolAddress = {
            address: formData.schoolAddress,
            city: formData.schoolCity,
            state: formData.schoolState,
            zipCode: formData.schoolZipCode,
            schoolPhone: formData.schoolPhone || '',
            location: geocodeResult ? { lat: geocodeResult.lat, lng: geocodeResult.lng } : null
          };
        } catch (err) {
          console.error('Geocoding failed:', err);
          updateData.schoolAddress = {
            address: formData.schoolAddress,
            city: formData.schoolCity,
            state: formData.schoolState,
            zipCode: formData.schoolZipCode,
            schoolPhone: formData.schoolPhone || ''
          };
        }
      } else {
        // Clear school address if all fields are empty
        updateData.schoolAddress = null;
      }

      await projectService.updateProject(project.id, updateData);

      // Sync team members (bidirectional: project.assignedUsers <-> user.projects)
      await projectService.syncAssignedUsers(project.id, teamMembers, project.assignedUsers || []);

      // Generate amendment if requested
      if (generateAmendment && pendingChangedTerms.length > 0) {
        try {
          await contractGenerationService.generateAmendmentPDF(project.id, pendingChangedTerms);
          toast.success('Project updated and amendment draft created!');
        } catch (err) {
          console.error('Failed to generate amendment:', err);
          toast.success('Project updated! (Amendment generation failed — you can create it manually from Contracts page)');
        }
      } else {
        toast.success('Project updated successfully');
      }

      onProjectUpdated();
      onClose();
    } catch (error) {
      console.error('Error updating project:', error);
      toast.error('Failed to update project');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b flex justify-between items-center sticky top-0 bg-white">
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-bold">Edit Project</h2>
            {(() => {
              const config = PROJECT_TYPE_CONFIG[projectType];
              const Icon = config.icon;
              return (
                <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${config.color}`}>
                  <Icon className="w-4 h-4" />
                  {config.label}
                </span>
              );
            })()}
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Basic Info */}
          <div>
            <h3 className="text-lg font-medium mb-4">Project Details</h3>
            <div className="space-y-4">
              {/* Organization Selector - only for provider_admin */}
              {userProfile?.role === 'provider_admin' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <span className="flex items-center gap-2">
                      <Building2 className="w-4 h-4" />
                      Organization
                    </span>
                  </label>
                  <select
                    value={formData.organizationId}
                    onChange={(e) => setFormData({ ...formData, organizationId: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="">Canvassed (Provider)</option>
                    {organizations.map(org => (
                      <option key={org.id} value={org.id}>{org.name}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Select the organization this project belongs to.
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Project Name
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  rows={3}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                  className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="active">Active</option>
                  <option value="paused">Paused</option>
                  <option value="completed">Completed</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Project Timezone
                </label>
                <select
                  value={formData.timezone}
                  onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                >
                  {US_TIMEZONES.map(tz => (
                    <option key={tz.value} value={tz.value}>
                      {tz.label} ({getTimezoneAbbreviation(tz.value)})
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  All appointment times will display in this timezone.
                </p>
              </div>
            </div>
          </div>

          {/* Goals - Conditional based on project type */}
          <div>
            <h3 className="text-lg font-medium mb-4">Goals & Targets</h3>
            <div className="grid grid-cols-2 gap-4">

              {/* Lead Program & Other: Show Lead Goals */}
              {(projectType === 'lead_program' || projectType === 'other') && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Valid Lead Goal
                    </label>
                    <input
                      type="number"
                      value={formData.validLeads}
                      onChange={(e) => setFormData({ ...formData, validLeads: parseInt(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Conversion Rate Target (%)
                    </label>
                    <input
                      type="number"
                      value={formData.conversionRateTarget}
                      onChange={(e) => setFormData({ ...formData, conversionRateTarget: parseInt(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                </>
              )}

              {/* Enrollment: Show Enrollment Goals */}
              {projectType === 'enrollment' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Enrollment Target
                    </label>
                    <input
                      type="number"
                      value={formData.enrollmentTarget}
                      onChange={(e) => setFormData({ ...formData, enrollmentTarget: parseInt(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Conversion Rate Target (%)
                    </label>
                    <input
                      type="number"
                      value={formData.conversionRateTarget}
                      onChange={(e) => setFormData({ ...formData, conversionRateTarget: parseInt(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                </>
              )}

              {/* Survey: Show Survey-specific Goals */}
              {projectType === 'survey' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Total Doors to Visit
                    </label>
                    <input
                      type="number"
                      value={formData.totalDoors}
                      onChange={(e) => {
                        const doors = parseInt(e.target.value) || 0;
                        const calculatedGoal = Math.round(doors * (formData.anticipatedSurveyRate / 100));
                        setFormData({ ...formData, totalDoors: doors, surveyGoal: calculatedGoal });
                      }}
                      className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Anticipated Survey Rate (%)
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="100"
                      value={formData.anticipatedSurveyRate}
                      onChange={(e) => {
                        const rate = parseInt(e.target.value) || 0;
                        const calculatedGoal = Math.round(formData.totalDoors * (rate / 100));
                        setFormData({ ...formData, anticipatedSurveyRate: rate, surveyGoal: calculatedGoal });
                      }}
                      className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Expected percentage of doors that complete surveys
                    </p>
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Survey Goal
                    </label>
                    <div className="flex items-center gap-4">
                      <input
                        type="number"
                        value={formData.surveyGoal}
                        onChange={(e) => setFormData({ ...formData, surveyGoal: parseInt(e.target.value) || 0 })}
                        className="flex-1 px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                      />
                      <span className="text-sm text-gray-500">
                        Calculated: {Math.round(formData.totalDoors * (formData.anticipatedSurveyRate / 100))} surveys
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      Override the calculated goal if needed
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Schools / Campuses (segments) — not relevant to survey projects */}
          {projectType !== 'survey' && (
            <SchoolSegmentsEditor
              segments={schoolSegments}
              onChange={setSchoolSegments}
              projectType={projectType}
              defaultConversionRateTarget={formData.conversionRateTarget}
            />
          )}

          {/* Client CSV export toggle */}
          {projectType !== 'survey' && (
            <div className="flex items-start gap-2">
              <input
                type="checkbox"
                id="dailyHubspotExport"
                checked={formData.dailyHubspotExport}
                onChange={(e) => setFormData({ ...formData, dailyHubspotExport: e.target.checked })}
                className="mt-1 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <label htmlFor="dailyHubspotExport" className="text-sm text-gray-700">
                Attach client CSV (HubSpot format) to the daily recap email
                <span className="block text-xs text-gray-500">
                  Sends a HubSpot-import CSV of the day's leads with the nightly roll-up. Enable only for clients using that format (e.g. Clearwater/HC).
                </span>
              </label>
            </div>
          )}

          {/* Automated client emails toggle */}
          <div className="flex items-start gap-2">
            <input
              type="checkbox"
              id="automatedEmailsEnabled"
              checked={formData.automatedEmailsEnabled}
              onChange={(e) => setFormData({ ...formData, automatedEmailsEnabled: e.target.checked })}
              className="mt-1 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
            />
            <label htmlFor="automatedEmailsEnabled" className="text-sm text-gray-700">
              Send automated client emails (nightly lead recap)
              <span className="block text-xs text-gray-500">
                Off by default. Leave off while the project is in setup so clients don't get test emails — turn on at go-live. (Invoices always require manual approval regardless.)
              </span>
            </label>
          </div>

          {/* Per-household reporting toggle — default OFF (per-student) */}
          <div className="flex items-start gap-2">
            <input
              type="checkbox"
              id="countPerHousehold"
              checked={formData.countPerHousehold}
              onChange={(e) => setFormData({ ...formData, countPerHousehold: e.target.checked })}
              className="mt-1 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
            />
            <label htmlFor="countPerHousehold" className="text-sm text-gray-700">
              Count reports per household (not per student)
              <span className="block text-xs text-gray-500">
                For signature/interest projects where the contract unit is a signed household. Enrollment projects (per-student billing) leave this off.
              </span>
            </label>
          </div>

          {/* Appointment emails toggle — default OFF */}
          <div className="flex items-start gap-2">
            <input
              type="checkbox"
              id="appointmentEmails"
              checked={formData.appointmentEmails}
              onChange={(e) => setFormData({ ...formData, appointmentEmails: e.target.checked })}
              className="mt-1 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
            />
            <label htmlFor="appointmentEmails" className="text-sm text-gray-700">
              Send appointment emails (booking/reschedule confirmations + daily reminders)
              <span className="block text-xs text-gray-500">
                Off by default. Emails the parent a confirmation and notifies scheduled staff on every booking, reschedule, and morning-of reminder. Leave off for projects without tours/calls.
              </span>
            </label>
          </div>

          {/* Per-capture lead email toggle — default OFF */}
          <div className="flex items-start gap-2">
            <input
              type="checkbox"
              id="leadCapturedEmails"
              checked={formData.leadCapturedEmails}
              onChange={(e) => setFormData({ ...formData, leadCapturedEmails: e.target.checked })}
              className="mt-1 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
            />
            <label htmlFor="leadCapturedEmails" className="text-sm text-gray-700">
              Send an email for every captured lead
              <span className="block text-xs text-gray-500">
                Off by default. Pings assigned staff the moment a lead is captured. High-volume field days = many emails — the daily recap usually covers it.
              </span>
            </label>
          </div>

          {/* Auto-invoice toggle */}
          {canEditBilling && (
            <div className="flex items-start gap-2">
              <input
                type="checkbox"
                id="autoInvoiceEnabled"
                checked={formData.autoInvoiceEnabled}
                onChange={(e) => setFormData({ ...formData, autoInvoiceEnabled: e.target.checked })}
                className="mt-1 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <label htmlFor="autoInvoiceEnabled" className="text-sm text-gray-700">
                Auto-generate weekly invoices for this project
                <span className="block text-xs text-gray-500">
                  Off by default. When on, the Monday job creates a draft invoice for this project each week. Leave off to bill manually.
                </span>
              </label>
            </div>
          )}

          {/* Billing - Only visible to provider_admin */}
          {canEditBilling && (
            <div>
              <h3 className="text-lg font-medium mb-4">Billing Configuration</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {projectType === 'survey'
                      ? 'Bill Rate per Survey ($)'
                      : projectType === 'enrollment'
                        ? 'Bill Rate per Enrollment ($)'
                        : 'Bill Rate per Valid Lead ($)'}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.ratePerValidLead}
                    onChange={(e) => setFormData({ ...formData, ratePerValidLead: parseFloat(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                {isProvider && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {projectType === 'survey'
                        ? 'Pay Rate per Survey ($)'
                        : projectType === 'enrollment'
                          ? 'Pay Rate per Enrollment ($)'
                          : 'Pay Rate per Valid Lead ($)'}
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.payRate}
                      onChange={(e) => setFormData({ ...formData, payRate: parseFloat(e.target.value) })}
                      className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                )}

                <div className={isProvider ? "col-span-2" : ""}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Payment Terms
                  </label>
                  <select
                    value={formData.paymentTerms}
                    onChange={(e) => setFormData({ ...formData, paymentTerms: e.target.value as any })}
                    className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="weekly">Weekly</option>
                    <option value="biweekly">Bi-weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Validation Settings */}
          <div>
            <h3 className="text-lg font-medium mb-4">Validation Settings</h3>
            <div className="space-y-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="requireValidation"
                  checked={formData.requireValidation}
                  onChange={(e) => setFormData({ ...formData, requireValidation: e.target.checked })}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label htmlFor="requireValidation" className="ml-2 block text-sm text-gray-900">
                  Require lead validation
                </label>
              </div>

              {formData.requireValidation && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contact Deadline (business days)
                  </label>
                  <input
                    type="number"
                    min={1}
                    value={formData.validationDeadlineDays}
                    onChange={(e) => setFormData({ ...formData, validationDeadlineDays: parseInt(e.target.value) || 2 })}
                    className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Business days to contact a lead before auto-validation for billing. Weekends are excluded.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Project Team */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Users className="w-5 h-5 text-indigo-600" />
              <h3 className="text-lg font-medium">Project Team</h3>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              Manage team members assigned to this project. Members receive email notifications for appointments and other project events.
            </p>

            {/* Current team members */}
            <div className="space-y-2 mb-4">
              {teamMembers.length === 0 ? (
                <p className="text-sm text-gray-400 italic">No team members assigned.</p>
              ) : (
                teamMembers.map((member) => {
                  const user = orgUsers.find(u => u.uid === member.userId);
                  return (
                    <div key={member.userId} className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-2">
                      <div>
                        <span className="text-sm font-medium text-gray-900">
                          {user?.name || member.userId}
                        </span>
                        {user?.email && (
                          <span className="text-xs text-gray-500 ml-2">{user.email}</span>
                        )}
                        <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-medium ${
                          member.role === 'provider_admin' ? 'bg-purple-100 text-purple-800' :
                          member.role === 'provider_user' ? 'bg-blue-100 text-blue-800' :
                          member.role === 'client_admin' ? 'bg-green-100 text-green-800' :
                          member.role === 'field' ? 'bg-orange-100 text-orange-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {member.role.replace('_', ' ')}
                        </span>
                      </div>
                      <button
                        onClick={() => setTeamMembers(prev => prev.filter(m => m.userId !== member.userId))}
                        className="p-1 hover:bg-gray-200 rounded text-gray-400 hover:text-red-600"
                        title="Remove from project"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  );
                })
              )}
            </div>

            {/* Add user dropdown */}
            <div className="flex items-center gap-2">
              <select
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                className="flex-1 px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                disabled={loadingOrgUsers}
              >
                <option value="">
                  {loadingOrgUsers ? 'Loading users...' : 'Select a user to add...'}
                </option>
                {orgUsers
                  .filter(u => !teamMembers.some(m => m.userId === u.uid))
                  .map(u => (
                    <option key={u.uid} value={u.uid}>
                      {u.name} ({u.role.replace('_', ' ')}) — {u.email}
                    </option>
                  ))}
              </select>
              <button
                onClick={() => {
                  if (!selectedUserId) return;
                  const user = orgUsers.find(u => u.uid === selectedUserId);
                  if (!user) return;
                  setTeamMembers(prev => [...prev, {
                    userId: user.uid,
                    role: user.role,
                    permissions: [],
                    assignedAt: Timestamp.now()
                  }]);
                  setSelectedUserId('');
                }}
                disabled={!selectedUserId}
                className="px-3 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 text-sm"
              >
                <UserPlus className="w-4 h-4" />
                Add
              </button>
            </div>
          </div>

          {/* School/Location Address */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <MapPin className="w-5 h-5 text-indigo-600" />
              <h3 className="text-lg font-medium">School/Location Address</h3>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              This address is used to center the map when creating saturation workloads.
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Street Address
                </label>
                <input
                  type="text"
                  value={formData.schoolAddress}
                  onChange={(e) => setFormData({ ...formData, schoolAddress: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="123 Main St"
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    City
                  </label>
                  <input
                    type="text"
                    value={formData.schoolCity}
                    onChange={(e) => setFormData({ ...formData, schoolCity: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="City"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    State
                  </label>
                  <input
                    type="text"
                    value={formData.schoolState}
                    onChange={(e) => setFormData({ ...formData, schoolState: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="CA"
                    maxLength={2}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    ZIP Code
                  </label>
                  <input
                    type="text"
                    value={formData.schoolZipCode}
                    onChange={(e) => setFormData({ ...formData, schoolZipCode: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="12345"
                    maxLength={10}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <span className="flex items-center gap-1">
                    <Phone className="w-3.5 h-3.5" />
                    School Phone
                  </span>
                </label>
                <input
                  type="tel"
                  value={formData.schoolPhone}
                  onChange={(e) => setFormData({ ...formData, schoolPhone: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="(555) 123-4567"
                />
                <p className="text-xs text-gray-500 mt-1">
                  This phone number will be included in appointment confirmation emails to parents.
                </p>
              </div>
              {project.schoolAddress?.location && (
                <p className="text-xs text-green-600">
                  Location coordinates saved: {project.schoolAddress.location.lat.toFixed(5)}, {project.schoolAddress.location.lng.toFixed(5)}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="p-6 border-t flex justify-end space-x-3 sticky bottom-0 bg-white">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 flex items-center"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>

    {/* Amendment Confirm Modal */}
    {showAmendmentModal && (
      <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60]">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
          <div className="p-4 border-b flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            <h3 className="text-lg font-semibold text-gray-900">Service Agreement Changes</h3>
          </div>
          <div className="p-4">
            <p className="text-sm text-gray-600 mb-4">
              These changes affect the service agreement terms. An amendment will be generated for the client to sign.
            </p>
            <table className="w-full text-sm border border-gray-200 rounded-md overflow-hidden">
              <thead>
                <tr className="bg-gray-50">
                  <th className="text-left px-3 py-2 font-medium text-gray-700">Field</th>
                  <th className="text-left px-3 py-2 font-medium text-gray-700">Old Value</th>
                  <th className="text-left px-3 py-2 font-medium text-gray-700">New Value</th>
                </tr>
              </thead>
              <tbody>
                {pendingChangedTerms.map((change, i) => (
                  <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-3 py-2 text-gray-800">{change.field}</td>
                    <td className="px-3 py-2 text-red-600 line-through">{change.oldValue}</td>
                    <td className="px-3 py-2 text-green-600 font-medium">{change.newValue}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="p-4 border-t flex flex-col gap-2">
            <button
              onClick={async () => {
                setShowAmendmentModal(false);
                setGeneratingAmendment(true);
                await doSave(true);
                setGeneratingAmendment(false);
              }}
              disabled={saving || generatingAmendment}
              className="w-full px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {generatingAmendment ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Generating Amendment...
                </>
              ) : (
                <>
                  <FileText className="w-4 h-4" />
                  Save & Generate Amendment
                </>
              )}
            </button>
            <button
              onClick={async () => {
                setShowAmendmentModal(false);
                await doSave(false);
              }}
              disabled={saving}
              className="w-full px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
            >
              Save Without Amendment
            </button>
            <button
              onClick={() => {
                setShowAmendmentModal(false);
                setPendingChangedTerms([]);
              }}
              className="w-full px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

